import { ChangeEvent, useState } from 'react';

import { Auth } from 'aws-amplify';

import './ChangePasswordForm.scss';
import { Button, UiInput } from 'ui-kit';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

interface ChangePasswordFormProps {
  setIsOpen: (isOpen: boolean) => void;
  initialError?: string;
  initialOldPassword?: string;
  initialNewPassword?: string;
  initialConfirmPassword?: string;
}

// A local props type rather than Omit<UiInputProps, ...>: UiInputProps has a
// catch-all [key: string]: any index signature, which makes Omit collapse
// away its actually-required fields (value, variant) — TS then can't verify
// they're present when spread. This is just the props PasswordInput's three
// real callers pass.
interface PasswordInputProps {
  placeholder: string;
  value: string;
  autoComplete: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

// variant="dark" + size="extra-large" + className="font-heebo" match this
// app's own convention for password fields (see SignInForm.tsx). UiInput has
// no built-in show/hide toggle, so that part stays a thin wrapper.
//
// containerClass="w-full": UiInput's outer div has no width of its own, so
// as a row-flex child of .change__password__input (display: flex) it
// shrink-wraps to its content instead of filling the row — unlike the old
// bare <input>, which a since-removed .change__password__input > input {
// width: 100% } rule stretched directly (input is nested two levels deeper
// now, so that rule stopped matching anything). Without this the field
// visually detaches from the show/hide button pinned at the row's right
// edge.
const PasswordInput = (props: PasswordInputProps) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  return (
    <div className="change__password__input">
      <UiInput
        {...props}
        variant="dark"
        size="extra-large"
        className="font-heebo"
        containerClass="w-full"
        type={passwordVisible ? 'text' : 'password'}
      />
      <button
        type="button"
        className="change__password__showbtn"
        onClick={() => setPasswordVisible(!passwordVisible)}
      >
        {passwordVisible ? <IconEyeOff /> : <IconEye />}
      </button>
    </div>
  );
};

const ChangePasswordForm = ({
  setIsOpen,
  initialError = '',
  initialOldPassword = '',
  initialNewPassword = '',
  initialConfirmPassword = '',
}: ChangePasswordFormProps) => {
  const [inProgress, setInProgress] = useState(false);
  const [oldPassword, setOldPassword] = useState(initialOldPassword);
  const [newPassword, setNewPassword] = useState(initialNewPassword);
  const [confirmPassword, setConfirmPassword] = useState(initialConfirmPassword);
  const [error, setError] = useState(initialError);

  async function changePassword(oldPassword: string, newPassword: string, confirmPassword: string) {
    let closeModal = false;
    setError('');
    setInProgress(true);
    try {
      if (!oldPassword) {
        setError('Old password cannot be empty.');
        return;
      }

      if (!newPassword || !confirmPassword) {
        setError('New password cannot be empty.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
      closeModal = true;
    } catch (err: unknown) {
      let message = 'Unknown error';
      if (err instanceof Error) message = err.message;
      setError(message);
    } finally {
      setInProgress(false);
      if (closeModal) {
        setIsOpen(false);
      }
    }
  }

  return (
    <form>
      <div className="change__password">
        {/* For browser password-manager autofill association only: a hidden
            username field paired with the password fields below, so
            browsers group this form as a password change rather than a
            fresh account. display: none previously made this inert --
            Chrome/Safari don't run autofill heuristics on display:none
            fields -- sr-only keeps it out of the visual layout while
            staying in the DOM and autofill-eligible. readOnly +
            autoComplete="username" (not "email": there's no real value
            here) is the standard hidden-username pattern. The sr-only
            wraps change__password__input rather than combining with it on
            one element, so its position/display don't fight sr-only's own
            (both single-class, load-order-dependent).
            tabIndex={-1} + aria-hidden: sr-only only hides visually, it
            doesn't pull the field out of tab order or the accessibility
            tree, so without these a keyboard user tabs through an
            invisible field before reaching "Old password" and a screen
            reader announces a "Username" control that isn't part of this
            flow. Autofill grouping only needs the field present with the
            right autoComplete value -- it doesn't need to be reachable. */}
        <div className="sr-only" aria-hidden="true">
          <div className="change__password__input">
            <UiInput
              variant="dark"
              value=""
              onChange={() => {}}
              readOnly
              tabIndex={-1}
              placeholder="Username"
              autoComplete="username"
            />
          </div>
        </div>
        <PasswordInput
          placeholder="Old password"
          value={oldPassword}
          autoComplete="current-password"
          onChange={(event) => {
            setOldPassword(event.target.value);
          }}
        />
        <PasswordInput
          placeholder="New password"
          value={newPassword}
          autoComplete="new-password"
          onChange={(event) => {
            setNewPassword(event.target.value);
          }}
        />
        <PasswordInput
          placeholder="Confirm password"
          value={confirmPassword}
          autoComplete="new-password"
          onChange={(event) => {
            setConfirmPassword(event.target.value);
          }}
        />
        {error && <div className="change__password__error">{error}</div>}
        <div className="change__password__button">
          <Button
            size="medium"
            label="Save Password"
            disabled={inProgress || !oldPassword || !newPassword || !confirmPassword}
            onClick={() => changePassword(oldPassword, newPassword, confirmPassword)}
          >
            Save Password
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
